import { useEffect, useState, useCallback } from "react";
import { Zap, Plus, Loader2, Trash2, Play, ArrowRight, Power } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";

export default function Automations() {
  const [autos, setAutos] = useState([]);
  const [options, setOptions] = useState({ triggers: [], actions: [] });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(null);

  const [form, setForm] = useState({ name: "", trigger_type: "", action_type: "", url: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, o] = await Promise.all([api.get("/automations"), api.get("/automations/options")]);
      setAutos(a.data.automations || []);
      setOptions(o.data);
      setForm((f) => ({
        ...f,
        trigger_type: f.trigger_type || o.data.triggers[0]?.type || "",
        action_type: f.action_type || o.data.actions.find((x) => x.status === "available")?.type || "",
      }));
    } catch { toast.error("Failed to load automations"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const labelOf = (list, type) => list.find((x) => x.type === type)?.label || type;

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name your automation");
    setCreating(true);
    try {
      const body = {
        name: form.name, trigger_type: form.trigger_type, action_type: form.action_type,
        action_params: form.action_type === "webhook" ? { url: form.url } : {},
      };
      await api.post("/automations", body);
      toast.success("Automation created");
      setForm({ name: "", trigger_type: options.triggers[0]?.type || "", action_type: options.actions.find((x) => x.status === "available")?.type || "", url: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };

  const toggle = async (a) => {
    try { await api.patch(`/automations/${a.automation_id}`, { enabled: !a.enabled }); await load(); }
    catch { toast.error("Update failed"); }
  };
  const test = async (a) => {
    setBusy(a.automation_id);
    try { const r = await api.post(`/automations/${a.automation_id}/test`); toast.success(r.data.message || "Test fired"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setBusy(null); }
  };
  const remove = async (a) => {
    try { await api.delete(`/automations/${a.automation_id}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };

  const isWebhook = form.action_type === "webhook";

  return (
    <Layout>
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Zap size={18} className="text-[color:var(--ws-accent,#4da3ff)]" />
          <p className="text-[color:var(--ws-accent,#4da3ff)] text-xs font-semibold uppercase tracking-[0.2em]">Automations</p>
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-white" data-testid="automations-title">Rules that work for you</h1>
        <p className="text-zinc-400 text-base mt-1">When something happens, do something automatically. No code required.</p>
      </div>

      {/* Builder */}
      <div className="bg-[#121212] border border-white/10 rounded-xl p-5 mb-6" data-testid="automation-builder">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-end">
          <div className="lg:col-span-3">
            <label className="block text-zinc-400 text-xs mb-1.5">Name</label>
            <input data-testid="auto-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. SSL alert"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--ws-accent,#4da3ff)]/50" />
          </div>
          <div className="lg:col-span-4">
            <label className="block text-zinc-400 text-xs mb-1.5">When (trigger)</label>
            <select data-testid="auto-trigger-select" value={form.trigger_type} onChange={(e) => setForm({ ...form, trigger_type: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none">
              {options.triggers.map((t) => <option key={t.type} value={t.type}>{t.label}</option>)}
            </select>
          </div>
          <div className="lg:col-span-4">
            <label className="block text-zinc-400 text-xs mb-1.5">Then (action)</label>
            <select data-testid="auto-action-select" value={form.action_type} onChange={(e) => setForm({ ...form, action_type: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none">
              {options.actions.map((a) => <option key={a.type} value={a.type} disabled={a.status !== "available"}>{a.label}{a.status !== "available" ? " (needs integration)" : ""}</option>)}
            </select>
          </div>
          <div className="lg:col-span-1">
            <button data-testid="auto-create-btn" onClick={create} disabled={creating}
              className="w-full btn-brand text-white rounded-lg px-3 py-2 text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
              {creating ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
            </button>
          </div>
          {isWebhook && (
            <div className="lg:col-span-12">
              <label className="block text-zinc-400 text-xs mb-1.5">Webhook URL</label>
              <input data-testid="auto-webhook-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://hooks.example.com/…"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--ws-accent,#4da3ff)]/50" />
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#007AFF]" /></div>
      ) : autos.length === 0 ? (
        <div className="text-center py-16 text-zinc-500" data-testid="automations-empty">
          <Zap size={40} className="mx-auto mb-3 text-zinc-600" />
          <p className="text-white font-medium">No automations yet.</p>
          <p className="text-sm mt-1">Create your first rule above — e.g. "When a site goes down → send a webhook".</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="automations-list">
          {autos.map((a) => (
            <div key={a.automation_id} data-testid={`automation-${a.automation_id}`} className="bg-[#121212] border border-white/10 rounded-xl p-4 flex items-center gap-4 flex-wrap">
              <button data-testid={`toggle-${a.automation_id}`} onClick={() => toggle(a)}
                className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${a.enabled ? "bg-[#32D74B]/15 text-[#32D74B]" : "bg-white/5 text-zinc-500"}`}>
                <Power size={16} />
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-white font-medium">{a.name}</p>
                <div className="flex items-center gap-2 text-xs text-zinc-500 mt-0.5 flex-wrap">
                  <span>{labelOf(options.triggers, a.trigger_type)}</span>
                  <ArrowRight size={12} />
                  <span>{labelOf(options.actions, a.action_type)}</span>
                  {a.action_type === "webhook" && a.action_params?.url && <span className="font-mono text-zinc-600 truncate max-w-[240px]">· {a.action_params.url}</span>}
                </div>
              </div>
              <span className="text-xs text-zinc-500">Fired {a.run_count || 0}×</span>
              <div className="flex items-center gap-2">
                <button data-testid={`test-${a.automation_id}`} onClick={() => test(a)} disabled={busy === a.automation_id}
                  className="text-sm text-zinc-300 hover:text-white border border-white/15 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors">
                  {busy === a.automation_id ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Test
                </button>
                <button data-testid={`delete-${a.automation_id}`} onClick={() => remove(a)} className="text-zinc-500 hover:text-[#FF3B30] transition-colors p-1.5">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
